/**
 * BTK Cabinet Thumbnail - Kitchen Units Listing Manager
 * ----------------------------------------------------------------
 * Lazy-renders 3D cabinet thumbnails on top of the existing product
 * cards in the kitchen-units listing. Mirrors the pattern used by
 * the planner's Add Units popup: IntersectionObserver gates visibility,
 * cap concurrency at 2 to keep the GPU calm, LRU-cache results so
 * scrolling back is instant, and invalidate on buying-style change.
 *
 * Relies on the headless renderer bootstrap to expose:
 *   window.btkRenderCabinetThumbnail(cab, opts)  → Promise<dataURL>
 *   window.btkThumbnailCatalogue                  → Catalogue API
 *
 * Card hooks (set by snippets/btk-product-card.liquid):
 *   <article class="bku-card" data-btk-card
 *            data-btk-thumb-unit-type-handle="..."
 *            data-btk-thumb-product-kitchen-style="...">
 *     <img class="bku-img-front" data-bku-main-image src="..." />
 *     ...
 *     <button class="bku-add" data-variant-id="...">Add</button>
 *
 * The Add button's data-variant-id always reflects the currently-
 * selected variant (the listing's own JS keeps it in sync as pills
 * click). We watch that with a MutationObserver per card so width /
 * depth / height pill clicks trigger a fresh thumbnail render.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.__btkListingThumbInit) return;
  window.__btkListingThumbInit = true;

  // ---- Cache + queue (same shape as the planner popup) ----------
  const MAX_CONCURRENT = 2;
  const LRU_LIMIT      = 200;
  const cache    = new Map();   // key → dataUrl (LRU; oldest first)
  const inFlight = new Set();
  const waiters  = new Map();   // key → more elements showing that picture
  const queue    = [];          // { key, cab, imgEl, framingDims }
  let   running  = 0;

  const pending  = new WeakMap();   // imgEl → { card }  (cab/framing built lazily)
  const cardWatchers = new WeakSet(); // cards with a MutationObserver attached

  // Per-card "doors open" UI state (local-only - not persisted, resets
  // on page nav). Keyed by card element so a stale card after AJAX
  // grid swap is GC'd automatically.
  const doorsOpenByCard = new WeakMap();

  // IntersectionObserver gates the EXPENSIVE buildCabForCard call.
  // On AJAX category change, processCard just observes each new
  // card's img - it does NO catalogue lookups up front. The
  // catalogue work only runs when the img scrolls into view. This
  // keeps L2 tile clicks snappy even on big category lists.
  const observer = (typeof IntersectionObserver !== 'undefined')
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          const p  = pending.get(el);
          observer.unobserve(el);
          pending.delete(el);
          if (!p || !p.card) continue;
          const Catalogue = window.btkThumbnailCatalogue;
          if (!Catalogue) continue;
          const cab = buildCabForCard(p.card, Catalogue);
          if (!cab) { setLoading(el, false); continue; }
          const fd  = buildFramingDimsForCard(p.card, Catalogue);
          requestThumb(el, cab, fd);
        }
      }, { rootMargin: '200px' })
    : null;

  function thumbKey(cab, fd) {
    if (!cab) return '';
    const h = cab.handleSpec || {};
    return [
      cab.unitTypeId, cab.variantId,
      cab.widthMm, cab.heightMm, cab.depthMm,
      cab.kitchenStyleId, cab.doorColourHandle, cab.carcaseColourHandle,
      h.designId, h.finishHandle, h.sizeMm,
      fd && fd.widthMm, fd && fd.heightMm, fd && fd.depthMm,
      cab._doorsOpen ? 'doors-open' : 'doors-closed',
      'hinge-' + (cab.hingeSide || 'right')
    ].join('|');
  }

  function paint(el, dataUrl) {
    if (!el || !el.isConnected || !dataUrl) return;
    if (el.tagName === 'IMG') {
      // Clear any responsive srcset/sizes first. When an <img> carries a
      // srcset with w-descriptors the browser selects from it and IGNORES
      // a later src= assignment - so without this the static featured
      // image would keep showing and the 3D render would never appear.
      // The card's initial srcset still optimises the pre-render download.
      if (el.hasAttribute('srcset')) el.removeAttribute('srcset');
      if (el.hasAttribute('sizes'))  el.removeAttribute('sizes');
      el.src = dataUrl;
      settleLoading(el);
      return;
    }
    // Element is a <canvas> left over from a still-swinging Open
    // Doors animation. Replace it with a fresh <img> carrying the
    // new render - keeps the data-bku-main-image marker intact so
    // selectors keep working for future renders.
    const img = document.createElement('img');
    img.alt = '';
    img.loading = 'lazy';
    if (el.className) img.className = el.className;
    if (el.style.width)  img.style.width  = el.style.width;
    if (el.style.height) img.style.height = el.style.height;
    for (const attr of el.attributes) {
      if (attr.name.startsWith('data-')) img.setAttribute(attr.name, attr.value);
    }
    img.src = dataUrl;
    el.replaceWith(img);
    settleLoading(img);
  }

  // ---- Loading state --------------------------------------------
  // A unit card still showing the placeholder drawing, with a 3D picture on
  // its way, shows the planner's spinner in its place until the picture lands
  // (.bku-card-media.is-loading - btk-kitchen-unit.css), and goes back to the
  // drawing if none can be drawn.
  function stillPlaceholder(el) {
    return !!(el && el.classList && el.classList.contains('bku-img-front--placeholder')
      && !/^data:/.test(el.getAttribute('src') || ''));
  }
  function setLoading(el, on) {
    const media = el && el.closest ? el.closest('.bku-card-media') : null;
    if (media) media.classList.toggle('is-loading', !!on && stillPlaceholder(el));
  }
  // The spinner gives way once the new picture is ready to show - not before,
  // or the drawing would flash back in between.
  function settleLoading(el) {
    const media = el && el.closest ? el.closest('.bku-card-media') : null;
    if (!media || !media.classList.contains('is-loading')) return;
    const done = () => media.classList.remove('is-loading');
    if (typeof el.decode === 'function') el.decode().then(done, done); else done();
  }

  function pump() {
    while (running < MAX_CONCURRENT && queue.length > 0) {
      const job = queue.shift();
      running++;
      run(job).finally(() => {
        running--;
        inFlight.delete(job.key);
        waiters.delete(job.key);
        pump();
      });
    }
  }
  async function run(job) {
    // Every element showing this picture: the one that asked, and any that
    // asked while it was being drawn (a grid redrawn mid-render).
    const slots = () => [job.imgEl, ...(waiters.get(job.key) || [])].filter(el => el.isConnected);
    if (!slots().length) return;
    if (typeof window.btkRenderCabinetThumbnail !== 'function') return;
    let dataUrl = null;
    try {
      // Default = closed; when the per-card Open Doors toggle is on,
      // render with doors swung open + drawers cascaded so the
      // static thumbnail matches the post-animation resting state.
      const openFronts = job.cab._doorsOpen
        ? { doors: true, drawers: true, doorProgress: 1, drawerProgress: 1 }
        : null;
      dataUrl = await window.btkRenderCabinetThumbnail(job.cab, {
        width: 320, height: 320,
        framingDims: job.framingDims || null,
        openFronts
      });
    } catch (_) {}
    if (!dataUrl) {
      // Nothing to draw: back to the drawing.
      slots().forEach(el => setLoading(el, false));
      return;
    }
    cache.set(job.key, dataUrl);
    if (cache.size > LRU_LIMIT) {
      const oldest = cache.keys().next().value;
      cache.delete(oldest);
    }
    if (typeof job.cab._hasOpenable === 'boolean') openableByKey.set(job.key, job.cab._hasOpenable);
    slots().forEach(el => { paint(el, dataUrl); syncOpenBtn(el, job.key); });
  }

  // The card's Open button shows only when its picture has a door or drawer
  // that opens (the renderer sets cab._hasOpenable). key -> true / false.
  const openableByKey = new Map();
  function syncOpenBtn(el, key) {
    if (!el || !openableByKey.has(key)) return;
    const card = el.closest('[data-btk-card]');
    const btn = card && card.querySelector('[data-btk-doors-toggle]');
    if (btn) btn.hidden = !openableByKey.get(key);
  }

  function requestThumb(imgEl, cab, framingDims) {
    if (!imgEl || !cab) return;
    const key = thumbKey(cab, framingDims);
    if (!key) return;
    const cached = cache.get(key);
    if (cached) {
      cache.delete(key); cache.set(key, cached);   // refresh LRU
      paint(imgEl, cached);
      syncOpenBtn(imgEl, key);
      return;
    }
    if (inFlight.has(key)) {
      // Another card is drawing this exact picture: it paints this one too.
      if (!waiters.has(key)) waiters.set(key, new Set());
      waiters.get(key).add(imgEl);
      return;
    }
    inFlight.add(key);
    queue.push({ key, cab, imgEl, framingDims });
    pump();
  }

  // ---- Synthetic cab from card + buying style -------------------

  function readBuyingCtx() {
    try { return JSON.parse(sessionStorage.getItem('btk_kitchen_context') || 'null'); }
    catch (_) { return null; }
  }

  // Build a synthetic cab for a card at its CURRENT variant. The
  // active buying style (from sessionStorage) drives the rendered
  // colours so customers see units in THEIR chosen style. Falls
  // back to the card's own kitchen style if no buying style is set.
  // PARTS WITH NO UNIT MODEL (Arden, 2026-10-09): end panels, cornice, pelmet,
  // plinth, end features, posts, fillers, doors sold on their own. They used to
  // get no picture at all. End panels go through the unit path below (the
  // renderer draws them from the style's panel model); the rest are drawn as
  // PARTS (renderCabinetThumbnail's thumbPart, the same as the basket):
  //   trim  - a 450mm length of the style's own profile model, found from the
  //           product's trim_profile (data-btk-trim-profile on the card);
  //   board - a flat board in the door colour, sized from the dimensions in
  //           the product's name ("900 x 650 x 18mm"), a door's height
  //           ("715mm High Door"), else a post.
  const PART_SIZE_RE = /(\d{2,4})\s*(?:mm)?\s*x\s*(\d{2,4})(?:\s*(?:mm)?\s*x\s*(\d{1,4}))?/i;
  // An island / breakfast-bar BACK panel is in the end-panels group but is a
  // long flat board (900 x 2400 x 18mm), not a side panel: drawn down the
  // end-panel route it came out a deep block (Arden, 2026-10-09). It is
  // drawn as a board, sized from its name.
  const isBackPanelType = (ut) => /back-panel/i.test(String((ut && ut.id) || ""));
  // A CORNER POST names no size. An Adjustable Corner Post is supplied as two
  // 70mm wide panels and a Fixed Corner Post as an L, so each is drawn that way
  // rather than as a 70mm square block (Arden, 2026-10-09). True Handleless
  // posts are door height (685mm), as the planner draws them.
  // An External Corner Post is an L too, but wraps an outside corner, so it is
  // seen from its outside; it names its size ("685 x 46 x 46mm" = height, legs).
  // A RADIUS or SQUARE END FEATURE ("2400 x 100 x 50mm") is the post that ends
  // a run: 50mm thick, 100mm deep, its 50mm face to the front, the radius one
  // with its front corner rounded - as the planner draws it. Sized from the
  // name it came out a plain board with its 100mm side forward, reading as
  // the back (Arden, 2026-10-09).
  // A RADIUS CORNICE BLOCK is the moulded corner piece that caps a radius end
  // post, not a length of curved cornice: drawn from its cornice profile's own
  // block model (the trim's radius_glb, as the planner places it), drawn on
  // its own, seen from below like the other cornice (Arden, 2026-10-09). The
  // profile is the one named in the title (Tangent, Universal - Modern, Classic...).
  function corniceBlockPart(title, Catalogue, styleId) {
    if (!/cornice\s+block/i.test(title)) return null;
    const list = ((Catalogue.getTrimForStyle?.(styleId) || {}).cornice || []).filter(x => x && x.radiusGlbUrl);
    const t = String(title).toLowerCase();
    // Trims are named "Cornice - Tangent"; the title says "Cornice Block -
    // Tangent", so match on the profile part alone.
    const prof = (x) => String(x.name || "").toLowerCase().replace(/^\s*cornice\s*-\s*/, "").trim();
    const named = list.filter(x => prof(x) && t.includes(prof(x)))
      .sort((a, b) => prof(b).length - prof(a).length);
    const e = named[0] || list[0];
    if (!e) return null;
    return { widthMm: 50, heightMm: 200, depthMm: 100, thumbPart: { kind: 'corniceblock', glbUrl: e.radiusGlbUrl, lookUp: true } };
  }
  // A size given in metres ("3m x 150mm", "2.8mx150") read as millimetres, so the size
  // match below finds it - else a tall filler fell back to the square post.
  const inMm = (title) => String(title || '').replace(/(\d+(?:\.\d+)?)\s*m(?!m)/gi,
    (_, n) => Math.round(Number(n) * 1000) + 'mm');
  function endFeaturePart(title) {
    if (/cornice/i.test(title)) return null;   // a Curved Cornice Block is a trim
    const kind = /radius\s+end\s+feature/i.test(title) ? 'radius'
      : /square\s+end\s+feature/i.test(title) ? 'square' : null;
    if (!kind) return null;
    const m = title.match(/(\d{3,4})\s*(?:mm)?\s*x/i);
    const H = m ? Math.min(3000, Number(m[1])) : 900;
    return { widthMm: 50, heightMm: H, depthMm: 100, thumbPart: { kind: 'endpost', shape: kind } };
  }
  function cornerPostPart(title) {
    const shape = /fixed\s+corner\s+post/i.test(title) ? 'l'
      : /adjustable\s+corner\s+post/i.test(title) ? 'pair'
      : /external\s+corner\s+post/i.test(title) ? 'ext' : null;
    if (!shape) return null;
    if (shape === 'ext') {
      const m = title.match(/(\d{2,4})\s*(?:mm)?\s*x\s*(\d{2,3})/i);
      const H = m ? Math.min(3000, Number(m[1])) : 720, L = m ? Number(m[2]) : 46;
      return { widthMm: L, heightMm: H, depthMm: L, thumbPart: { kind: 'post', shape, legMm: L } };
    }
    const H = /true\s+handleless/i.test(title) ? 685 : 720;
    return shape === 'l'
      ? { widthMm: 70, heightMm: H, depthMm: 70, thumbPart: { kind: 'post', shape } }
      : { widthMm: 128, heightMm: H, depthMm: 70, thumbPart: { kind: 'post', shape } };
  }
  function buildPartCabForCard(card, Catalogue) {
    const ctx = readBuyingCtx();
    const styleId = (ctx && ctx.style) || card.dataset.btkThumbProductKitchenStyle || null;
    if (!styleId) return null;
    const addBtn = card.querySelector('.bku-add[data-variant-id]');
    const rawVid = addBtn ? addBtn.dataset.variantId : null;
    if (!rawVid) return null;
    const variantId = Number.isFinite(Number(rawVid)) ? Number(rawVid) : rawVid;
    const doorColours = Catalogue.getDoorColoursForStyle?.(styleId) || [];
    const doorH = (ctx && ctx.colour) || doorColours[0]?.handle || null;
    const base = {
      type: 'cabinet', variantId, kitchenStyleId: styleId,
      x: 0, y: 0, rotationDeg: 0,
      doorColourHandle: doorH, carcaseColourHandle: null, hingeSide: 'right'
    };
    const profile = card.dataset.btkTrimProfile || '';
    const title = card.dataset.btkPartTitle || '';
    // A CURVED cornice / pelmet / plinth carries no trim_profile (it is not a
    // picker choice), so it is matched by name: the trim type, then the
    // profile named in the title (Universal, Tangent, Classic...), else the
    // style's first. Drawn curved - its curved model, or the straight one bent.
    const block = corniceBlockPart(title, Catalogue, styleId);
    if (block) return Object.assign({}, base, { id: 'listing-cblock-' + variantId, unitTypeId: 'part-cornice-block' }, block);
    const curvedFeat = /curved/i.test(title) && (title.match(/\b(cornice|pelmet|plinth)\b/i) || [])[1];
    // Checked BEFORE the profile route: an External Curved Plinth can carry the
    // straight profile's trim_profile, and that drew it straight (Arden, 2026-10-09).
    if (curvedFeat) {
      const tr = Catalogue.getTrimForStyle?.(styleId) || {};
      const list = tr[curvedFeat.toLowerCase()] || [];
      const t = title.toLowerCase();
      const e = (profile && list.find(x => x && x.handle === profile))
        || list.find(x => x && x.name && t.includes(String(x.name).toLowerCase()))
        || list.find(x => x && x.curvedGlbUrl) || list[0];
      if (!e || !(e.curvedGlbUrl || e.straightGlbUrl)) return null;
      return Object.assign({}, base, { id: 'listing-ctrim-' + variantId, unitTypeId: 'part-trim',
        widthMm: 300, heightMm: 150, depthMm: 300,
        thumbPart: { kind: 'trim', glbUrl: e.curvedGlbUrl || e.straightGlbUrl, curved: !!e.curvedGlbUrl, bend: !e.curvedGlbUrl,
                     lookUp: curvedFeat.toLowerCase() === 'cornice' } });
    }
    if (profile) {
      const tr = Catalogue.getTrimForStyle?.(styleId) || {};
      let e = null, feat = null;
      for (const f of ['cornice', 'pelmet', 'plinth']) {
        if (!e) { e = (tr[f] || []).find(x => x && x.handle === profile) || null; if (e) feat = f; }
      }
      if (!e || !e.straightGlbUrl) return null;
      return Object.assign({}, base, { id: 'listing-trim-' + variantId, unitTypeId: 'part-trim',
        widthMm: 450, heightMm: 150, depthMm: 40, thumbPart: { kind: 'trim', glbUrl: e.straightGlbUrl, lookUp: feat === 'cornice' } });
    }
    const post = cornerPostPart(title) || endFeaturePart(title);
    if (post) return Object.assign({}, base, { id: 'listing-post-' + variantId, unitTypeId: 'part-post' }, post);
    const m = inMm(title).match(PART_SIZE_RE);
    let H = 720, W = 70, T = 70;          // a post, when the name gives no size
    if (m) {
      H = Math.min(3000, Number(m[1])); W = Number(m[2]); T = m[3] ? Number(m[3]) : 18;
    } else {
      const hm = title.match(/(\d{2,4})\s*mm\s+High/i);
      if (hm) { H = Number(hm[1]); W = 450; T = 18; }
    }
    return Object.assign({}, base, { id: 'listing-board-' + variantId, unitTypeId: 'part-board',
      widthMm: W, heightMm: H, depthMm: T, thumbPart: { kind: 'board' } });
  }

  // One fresh catalogue fetch per page (btkRefreshCatalogueProducts), and at
  // most one retry per card, so a variant still missing after it can't loop.
  function refetchAndRedraw(card) {
    if (card.dataset.btkCatRefetched || !window.btkRefreshCatalogueProducts) return;
    card.dataset.btkCatRefetched = '1';
    window.btkRefreshCatalogueProducts().then(ok => {
      const Catalogue = window.btkThumbnailCatalogue;
      const target = card.querySelector('[data-bku-main-image]');
      if (!ok || !Catalogue || !target) return;
      const cab = buildCabForCard(card, Catalogue);
      if (cab) requestThumb(target, cab, buildFramingDimsForCard(card, Catalogue));
    });
  }

  function buildCabForCard(card, Catalogue) {
    const unitTypeHandle = card.dataset.btkThumbUnitTypeHandle;
    if (!unitTypeHandle) return null;
    const ut = Catalogue.getUnitType?.(unitTypeHandle);
    if (!ut) return null;
    const isEndPanel = ut.unitTypeGroupHandle === 'end-panels' && !isBackPanelType(ut);
    if (!(Catalogue.unitTypeHasPicture ? Catalogue.unitTypeHasPicture(ut) : !!ut.carcaseModelGlbUrl) && !isEndPanel) return buildPartCabForCard(card, Catalogue);

    const ctx = readBuyingCtx();
    const styleId  = (ctx && ctx.style)
      || card.dataset.btkThumbProductKitchenStyle
      || null;
    if (!styleId) return null;

    // Current variant id sits on the Add button. The listing's
    // own JS keeps this in sync as pills click. DOM dataset values
    // are STRINGS - the catalogue's variantInfoById Map is keyed
    // by NUMBER (from the JSON-injected `{{ v.id | json }}`), so a
    // string lookup misses every variant and the cab ends up with
    // null widthMm - which the renderer interprets as zero, hence
    // the "squished panel + legs" look. Cast to Number for the
    // lookup; fall back to the original if NaN (handles future
    // string-id variants gracefully).
    const addBtn = card.querySelector('.bku-add[data-variant-id]');
    const rawVid = addBtn ? addBtn.dataset.variantId : null;
    if (!rawVid) return null;
    const numVid = Number(rawVid);
    const variantId = Number.isFinite(numVid) ? numVid : rawVid;

    const def = Catalogue.buildCabinetDefinition({
      unitTypeId: ut.id, variantId, kitchenStyleId: styleId
    });
    // An end panel has no size variant: a thin footprint (its thickness along
    // the run) and the unit type's depth, as the basket draws it.
    if (def && isEndPanel) {
      if (!def.widthMm) def.widthMm = 18;
      if (!def.depthMm) def.depthMm = ut.defaultDepthMm || 650;
    }
    if (!def || !def.widthMm) {
      // Variant didn't resolve - either the catalogue is missing
      // this product (kitchen_style_handle metafield not set), or
      // the Width option is named differently. Skip silently; the
      // existing product image stays.
      // A variant the catalogue has never heard of is usually one pushed
      // after this tab cached it: refetch once and draw the card again.
      if (!Catalogue.getVariantInfo?.(variantId)) refetchAndRedraw(card);
      return null;
    }

    const doorColours    = Catalogue.getDoorColoursForStyle?.(styleId)    || [];
    // Per UNIT, not per style: a unit sold in only some carcase colours (the
    // Larder Pantry is Davenport Oak only) must not be drawn in the carcase
    // the shopper picked in the context bar if it isn't made in it.
    const carcaseColours = Catalogue.getCarcaseColoursForUnit?.(ut.id, styleId)
                        || Catalogue.getCarcaseColoursForStyle?.(styleId) || [];
    const doorH    = (ctx && ctx.colour)  || doorColours[0]?.handle    || null;
    const _ctxCarcase = (ctx && ctx.carcase) || null;
    const carcaseH = (carcaseColours.some(c => c.handle === _ctxCarcase) ? _ctxCarcase : null)
      || carcaseColours[0]?.handle || null;
    const heightMm = def.belfastSink
      ? (720 + (def.plinthHeightMm || 150))
      : (def.carcaseHeightMm || 720) + (def.plinthHeightMm || 0);
    // Corner-unit markers so thumbnails route through the corner-aware
    // GLB builders (blank-panel / diagonal / L-shape) instead of the
    // plain rectangular buildCabinetGLB. Same derivation as the planner
    // popup (buildSyntheticCabForThumb).
    const _shape = String(def.planner2D?.shape || '').trim().toLowerCase();
    let cornerUnit = null, diagonalCornerUnit = null, lShapeUnit = null, radiusUnit = null;
    let footprintWidthMm = def.widthMm, footprintDepthMm = def.depthMm;
    // CURVED units. Without this the shape fell through to the plain
    // rectangular builder, which draws its fronts from the cutout schema -
    // and the curved units carry no schema (the curve and its door come
    // from the shape alone), so a storefront thumbnail rendered a bare
    // carcase with NO DOOR. Same derivation the planner and the Add Units
    // popup use, so all three agree.
    if (_shape === 'radius-island' || _shape === 'radius-wall') {
      const RAD = 200;
      // Suffix names the run end capped, the INVERSE of the body-frame
      // hand, and real codes write it before a B/W carcase letter
      // (CV30LB / CV30RB / CV30RW). [BW]? keeps CV30OPB - open, no hand.
      const codeHand = /-?L[BW]?$/i.test(def.code || '') ? 'right'
                     : /-?R[BW]?$/i.test(def.code || '') ? 'left'
                     : 'right';
      radiusUnit = {
        kind: _shape === 'radius-wall' ? 'end-wall' : 'island-base',
        hand: codeHand, carcaseRadiusMm: RAD, doorRadiusMm: RAD
      };
      footprintWidthMm = def.widthMm || 300;
      footprintDepthMm = def.depthMm || 300;
    } else if (_shape === 'corner-blank' || _shape === 'diagonal' || _shape === 'lshape') {
      // The sizes the planner gives a placed unit - base 910 / 570, WALL 630 / 330
      // (one helper for every thumbnail; see Catalogue.cornerThumbMarkers).
      ({ cornerUnit, diagonalCornerUnit, lShapeUnit, footprintWidthMm, footprintDepthMm } = Catalogue.cornerThumbMarkers(def));
    }
    return {
      id: 'listing-thumb-' + ut.id + '-' + variantId,
      type: 'cabinet',
      unitTypeId:     def.unitTypeId,
      variantId:      def.variantId,
      kitchenStyleId: def.kitchenStyleId,
      code:           def.code,
      category:       def.categoryHandle,
      planner2D:      def.planner2D,
      x: 0, y: 0, rotationDeg: 0,
      widthMm:        footprintWidthMm,
      depthMm:        footprintDepthMm,
      heightMm,
      doorColourHandle:    doorH,
      carcaseColourHandle: carcaseH,
      cutoutSchema:   def.cutoutSchema,
      // Default thumbnails to user-perspective RIGHT hinge so the
      // open door swings to the LEFT (away from the mirrored camera
      // at world +X) and the cab interior reads cleanly. Catalog
      // default is ignored here on purpose - listing/detail/cart
      // should look consistent regardless of per-product setting.
      hingeSide:      'right',
      drawerCount:    def.drawerCount,
      belfastSink:    def.belfastSink,
      splayEnd:       def.splayEnd,
      cornerUnit,
      diagonalCornerUnit,
      lShapeUnit,
      radiusUnit,
      sinkBase:       def.sinkBase,
      _doorsOpen:     doorsOpenByCard.get(card) === true
    };
  }

  function buildFramingDimsForCard(card, Catalogue) {
    // Use the unit type's metaobject-declared variants to find the
    // LARGEST (w,h,d) - so width pill clicks render at consistent
    // scale and the user can see which variant is bigger.
    const unitTypeHandle = card.dataset.btkThumbUnitTypeHandle;
    if (!unitTypeHandle) return null;
    const ut = Catalogue.getUnitType?.(unitTypeHandle);
    if (!ut) return null;
    const ctx = readBuyingCtx();
    const styleId = (ctx && ctx.style) || card.dataset.btkThumbProductKitchenStyle || null;
    if (!styleId) return null;
    const product  = Catalogue.getProductForUnitInStyle?.(ut.id, styleId);
    if (!product || !Array.isArray(product.variants)) return null;
    const max = { widthMm: 0, heightMm: 0, depthMm: 0 };
    for (const v of product.variants) {
      const info = Catalogue.getVariantInfo?.(v.id);
      if (!info) continue;
      const w = info.widthMm || 0;
      const d = info.depthMm || 0;
      const h = (info.heightMm || ut.carcaseHeightMm || 720)
              + (ut.plinthHeightMm || 0);
      if (w > max.widthMm)  max.widthMm  = w;
      if (h > max.heightMm) max.heightMm = h;
      if (d > max.depthMm)  max.depthMm  = d;
    }
    return max;
  }

  // ---- Card lifecycle -------------------------------------------

  // Cheap pass: store the card reference, attach IntersectionObserver,
  // hook the Add-button variant change. No catalogue work yet.
  function processCard(card) {
    const imgEl = card.querySelector('img[data-bku-main-image]');
    if (!imgEl) return;
    // Spin while this card's 3D picture is on its way: only a unit type with a
    // 3D model gets one (a cheap lookup - the full check waits for the card to
    // come into view, and stops the spinner if the card can't be drawn).
    const Cat0 = window.btkThumbnailCatalogue;
    const ut0  = (Cat0 && card.dataset.btkThumbUnitTypeHandle) ? Cat0.getUnitType?.(card.dataset.btkThumbUnitTypeHandle) : null;
    setLoading(imgEl, !!(ut0 && (Cat0.unitTypeHasPicture ? Cat0.unitTypeHasPicture(ut0) : !!ut0.carcaseModelGlbUrl)));

    if (!observer) {
      // No IntersectionObserver - render straight away (defensive fallback).
      const Catalogue = window.btkThumbnailCatalogue;
      if (!Catalogue) return;
      const cab = buildCabForCard(card, Catalogue);
      if (!cab) { setLoading(imgEl, false); return; }
      const fd  = buildFramingDimsForCard(card, Catalogue);
      requestThumb(imgEl, cab, fd);
    } else {
      pending.set(imgEl, { card });
      observer.observe(imgEl);
    }

    // Re-render on variant change. The listing's own JS updates the
    // Add button's data-variant-id when pills click; a MutationObserver
    // catches that and re-triggers the thumbnail for the new variant.
    if (!cardWatchers.has(card)) {
      const addBtn = card.querySelector('.bku-add[data-variant-id]');
      if (addBtn) {
        const mo = new MutationObserver(() => {
          const Catalogue = window.btkThumbnailCatalogue;
          if (!Catalogue) return;
          // Re-query the current target each time - the IMG may have
          // been replaced with a <canvas> mid-swing, or a fresh <img>
          // after a swing completed. data-bku-main-image is preserved
          // across swaps so the attribute selector finds either form.
          const currentTarget = card.querySelector('[data-bku-main-image]');
          if (!currentTarget) return;
          const c2 = buildCabForCard(card, Catalogue);
          const fd2 = buildFramingDimsForCard(card, Catalogue);
          if (c2) requestThumb(currentTarget, c2, fd2);
        });
        mo.observe(addBtn, { attributes: true, attributeFilter: ['data-variant-id'] });
        cardWatchers.add(card);
      }
    }
  }

  // ---- Door & drawer front matrix rows ---------------------------
  // The door-front matrix (snippets/btk-door-front-matrix.liquid) had no
  // pictures - those products have none stored. Each row is drawn as the
  // style's own front model (renderer thumbPart 'front'), at the height in its
  // name ("715mm High Door", "140mm High Drawer Front") and the width pill
  // picked, in the buying colour; redrawn when another width is picked. It
  // paints into the row's fixed 74px image, so the row keeps its height
  // (Arden, 2026-10-09).
  function buildFrontCabForRow(row, Catalogue) {
    const img = row.querySelector('img[data-btk-dfm-thumb]');
    if (!img) return null;
    const title = img.dataset.btkPartTitle || '';
    const hm = title.match(/(\d{2,4})\s*mm\s+High/i);
    if (!hm) return null;
    const ctx = readBuyingCtx();
    const styleId = img.dataset.btkThumbProductKitchenStyle || (ctx && ctx.style) || null;
    if (!styleId) return null;
    const pill = row.querySelector('[data-dfm-width-btn].is-active') || row.querySelector('[data-dfm-width-btn]');
    const W = pill ? parseInt(pill.dataset.width, 10) : NaN;
    const doorColours = Catalogue.getDoorColoursForStyle?.(styleId) || [];
    const doorH = (ctx && ctx.style === styleId && ctx.colour) || doorColours[0]?.handle || null;
    return {
      id: 'dfm-front-' + row.dataset.productId, type: 'cabinet', unitTypeId: 'part-front',
      variantId: row.dataset.productId, kitchenStyleId: styleId,
      x: 0, y: 0, rotationDeg: 0,
      widthMm: W > 0 ? W : 450, heightMm: Math.min(3000, Number(hm[1])), depthMm: 18,
      doorColourHandle: doorH, carcaseColourHandle: null, hingeSide: 'right',
      thumbPart: { kind: 'front', role: /drawer/i.test(title) ? 'drawer' : 'door' }
    };
  }
  const dfmRows = new WeakSet();
  function processDfmRow(row) {
    if (dfmRows.has(row)) return;
    dfmRows.add(row);
    const draw = () => {
      const img = row.querySelector('img[data-btk-dfm-thumb]');
      const Catalogue = window.btkThumbnailCatalogue;
      if (!img || !Catalogue) return;
      const cab = buildFrontCabForRow(row, Catalogue);
      if (cab) requestThumb(img, cab, null);
    };
    draw();
    // The matrix's own click handler marks the pill active first.
    row.addEventListener('click', (e) => {
      if (e.target.closest && e.target.closest('[data-dfm-width-btn]')) setTimeout(draw, 0);
    });
  }

  function processAllCards() {
    document.querySelectorAll('[data-btk-card]').forEach(processCard);
    document.querySelectorAll('[data-dfm-row]').forEach(processDfmRow);
  }

  // ---- Boot ------------------------------------------------------
  //
  // The bootstrap is a deferred ES module - by the time the listing
  // section finishes parsing, the catalogue + headless renderer may
  // not be ready yet. Poll for window.btkThumbnailCatalogue and
  // start once it lands. Then walk the cards.
  function whenReady(cb) {
    if (window.btkThumbnailCatalogue && typeof window.btkRenderCabinetThumbnail === 'function') {
      cb(); return;
    }
    setTimeout(() => whenReady(cb), 60);
  }
  // Initial cards: process immediately. Cards added later (AJAX nav,
  // filter changes, pagination) are picked up by the grid observer +
  // the initBTKCards hook below. The IntersectionObserver inside
  // processCard handles the actual deferral - observing an off-screen
  // card is essentially free.
  function bootstrapInitialCards() {
    processAllCards();
  }

  // The listing page swaps #bku-grid.innerHTML for L2 navigation,
  // pagination, and filter changes (see fetchNewGrid in
  // sections/btk-units-listing.liquid). Two independent hooks make
  // this resilient:
  //   1. MutationObserver on #bku-grid catches every DOM swap.
  //   2. Monkey-patch on window.initBTKCards re-processes after the
  //      listing's own AJAX path completes - covers the case where
  //      the mutation observer misses anything.
  function attachAjaxHooks() {
    const grid = document.getElementById('bku-grid');
    if (grid) {
      const gridObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches?.('[data-btk-card]') ||
                node.querySelector?.('[data-btk-card]')) {
              processAllCards();
              return;
            }
          }
        }
      });
      gridObserver.observe(grid, { childList: true, subtree: true });
    }
    // A door-front page has the matrix in place of the grid.
    const region = document.getElementById('bku-ajax-region');
    if (region) {
      new MutationObserver(() => {
        if (region.querySelector('[data-dfm-row]')) processAllCards();
      }).observe(region, { childList: true, subtree: true });
    }
    // Wrap the listing's initBTKCards (called after every AJAX nav)
    // so re-processing runs even if MutationObserver misses the swap.
    if (typeof window.initBTKCards === 'function' && !window.__btkInitWrapped) {
      const orig = window.initBTKCards;
      window.initBTKCards = function(ctx) {
        try { orig(ctx); } catch (e) { console.warn('[BTK Thumb] initBTKCards threw', e); }
        processAllCards();
      };
      window.__btkInitWrapped = true;
    }
  }

  // The cards already on the page spin from the start - the renderer takes a
  // moment to arrive, and processCard settles each card once it has. If it
  // never arrives, stop after 20s and leave the drawings.
  document.querySelectorAll('[data-btk-card]').forEach(card => {
    if (card.dataset.btkThumbUnitTypeHandle) setLoading(card.querySelector('img[data-bku-main-image]'), true);
  });
  const loadingFailsafe = setTimeout(() => {
    document.querySelectorAll('.bku-card-media.is-loading').forEach(m => m.classList.remove('is-loading'));
  }, 20000);

  whenReady(() => {
    clearTimeout(loadingFailsafe);
    bootstrapInitialCards();
    attachAjaxHooks();
    // initBTKCards might not be defined yet at first-ready time —
    // re-try shortly so the wrapper always lands.
    setTimeout(attachAjaxHooks, 500);
    setTimeout(attachAjaxHooks, 1500);
  });

  // ─── Open Doors per-card toggle ──────────────────────────────────
  // Delegated click handler at document level so AJAX-injected cards
  // get the binding for free. Button sits inside .bku-card-media
  // (its parent <a>) - preventDefault/stopPropagation suppress the
  // anchor's "navigate to product" behaviour.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-btk-doors-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const card = btn.closest('[data-btk-card]');
    if (!card) return;
    const Catalogue = window.btkThumbnailCatalogue;
    if (!Catalogue) return;
    const imgEl = card.querySelector('img[data-bku-main-image]')
              || card.querySelector('canvas[data-bku-main-image]');
    if (!imgEl) return;

    const next = !(doorsOpenByCard.get(card) === true);
    if (next) doorsOpenByCard.set(card, true);
    else      doorsOpenByCard.delete(card);

    // Flip button visual immediately so the click feels responsive.
    btn.classList.toggle('bku-doors-toggle--on', next);
    btn.setAttribute('aria-pressed', next ? 'true' : 'false');
    const labelEl = btn.querySelector('.bku-doors-toggle__label');
    if (labelEl) labelEl.textContent = next ? 'Close' : 'Open';

    const cab = buildCabForCard(card, Catalogue);
    if (!cab) return;
    const fd = buildFramingDimsForCard(card, Catalogue);
    if (typeof window.btkAnimateCabinetThumbnail !== 'function') return;

    // OVERLAY APPROACH - don't replace the IMG. Instead, position a
    // transparent canvas absolutely over the IMG's exact pixel box.
    // The IMG stays visible underneath, so during the ~100-500ms
    // GLB-load wait before the animator's first frame, the user
    // still sees the static thumbnail (no white flash). Once the
    // animator starts painting opaque frames into the overlay
    // canvas (scene background is opaque white), the canvas content
    // covers the IMG and the swing is visible. After the animation
    // we remove the overlay and update IMG.src with the final frame.
    const cardRect   = card.getBoundingClientRect();
    const imgRect    = imgEl.getBoundingClientRect();
    // Offset parent for the overlay must be positioned (so absolute
    // children are positioned relative to it). .bku-card-media has
    // position:relative - use it. Falls back to the card itself.
    const cardMedia  = card.querySelector('.bku-card-media');
    const offsetParent = cardMedia || card;
    const offsetRect = offsetParent.getBoundingClientRect();
    const dpr        = window.devicePixelRatio || 1;
    const cssW       = Math.round(imgRect.width)  || 320;
    const cssH       = Math.round(imgRect.height) || 200;

    const canvas = document.createElement('canvas');
    canvas.width  = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.position      = 'absolute';
    canvas.style.top           = (imgRect.top  - offsetRect.top)  + 'px';
    canvas.style.left          = (imgRect.left - offsetRect.left) + 'px';
    canvas.style.width         = cssW + 'px';
    canvas.style.height        = cssH + 'px';
    canvas.style.zIndex        = '2';
    canvas.style.pointerEvents = 'none';
    canvas.dataset.btkListingSwingOverlay = '1';
    offsetParent.appendChild(canvas);

    let finalUrl = null;
    try {
      finalUrl = await window.btkAnimateCabinetThumbnail(cab, {
        width: cssW, height: cssH,
        framingDims: fd,
        fromProgress: next ? 0 : 1,
        toProgress:   next ? 1 : 0,
        durationMs:   340,
        targetCanvas: canvas
      });
    } catch (_) {}

    // Cache + paint IMG before removing overlay so there's no
    // moment of "overlay gone, IMG still showing old state".
    if (finalUrl) {
      const key = thumbKey(cab, fd);
      cache.set(key, finalUrl);
      if (cache.size > LRU_LIMIT) {
        const oldest = cache.keys().next().value;
        cache.delete(oldest);
      }
      if (imgEl.isConnected) paint(imgEl, finalUrl);
    }
    // Tiny delay before removing overlay lets the IMG's new src
    // load into the browser cache so the swap is invisible.
    setTimeout(() => { if (canvas.isConnected) canvas.remove(); }, 30);
  });

  // Buying-style change → wipe cache, cancel queue, repaint visible
  // cards. The new render uses the new colour scheme.
  document.addEventListener('btk:style-changed', () => {
    cache.clear();
    inFlight.clear();
    waiters.clear();
    queue.length = 0;
    processAllCards();
  });

  // When the page reloads with new URL params (e.g. the buying-style
  // drawer's switch flow), the DOM is fresh; the script auto-boots
  // again above.
})();
